import { useClerk, useOrganizationList } from '@clerk/clerk-react';
import { AccessScreen, useAutoWorkspaceSwitch, type ShareAdapter } from '@flobi/asset-library/share';
import { useEffect, useMemo, useRef, useState } from 'react';
import { touchSession } from '../auth/session';
import { artworkCmd } from '../services/apiClient';
import { Button } from '../ui/controls';
import { mayOpenByItself, watchForAccess, type AccessWatch } from './accessWatch';
import { artworkShareAdapter, fileThing, locateFile, type FileWorkspace } from './artworkShare';
import { rememberSwitchedFor, rememberSwitchedWorkspace, takeSwitchedFor } from './switched';

type Switcher = Required<Pick<ShareAdapter, 'findAccessibleWorkspace' | 'switchWorkspace'>>;

/**
 * The one screen for a file this person can't open here (SHARING-MODEL §3.3), as Moodboard
 * has it for a board:
 *
 *   - the file is in ANOTHER of their workspaces → switch to it, reload there (the editor is
 *     the session's workspace's), say "Switched to Acme Studio", and open the file. If they may
 *     not open it there either, the access screen is shown THERE, where Request access reaches
 *     the people who can approve it — and where approving it lets them in.
 *   - otherwise → "You need access to this file", with Request access.
 *
 * The backend answers "not one of yours" and "no such file" alike, so this tells someone
 * outside the file's workspace nothing the access screen did not already say. The switch is
 * Clerk's `setActive`, through its hook; no Clerk component is drawn.
 *
 * While the screen is up it waits for an answer (`watchForAccess`): once they are let in,
 * `onGranted` opens the file in place, without a reload.
 */
export function FileAccess({
  documentId,
  name,
  mode,
  onHome,
  onGranted,
}: {
  documentId: string;
  name: string | null;
  mode: 'light' | 'dark';
  onHome: () => void;
  onGranted: () => void;
}) {
  const { isLoaded, setActive, userMemberships } = useOrganizationList({ userMemberships: true });
  const session = useClerk();
  // Read at the moment of the switch: the adapter below must stay the same object, or the
  // search would start over (and drop its answer) whenever the memberships page in.
  const clerk = useRef({ setActive, session, memberships: userMemberships?.data ?? [] });
  clerk.current = { setActive, session, memberships: userMemberships?.data ?? [] };
  // Just switched for this very file: it is refused here too, so this is the answer.
  const [afterSwitch] = useState(() => takeSwitchedFor(documentId));

  const switcher = useMemo<Switcher>(() => {
    const found = new Map<string, FileWorkspace>();
    return {
      async findAccessibleWorkspace(thing) {
        const ws = await locateFile(thing.id);
        if (ws) found.set(ws.id, ws);
        return ws;
      },
      async switchWorkspace(workspaceId) {
        const ws = found.get(workspaceId);
        const { setActive: activate, session: current, memberships } = clerk.current;
        const org =
          memberships.find((m) => ws?.clerkOrgId && m.organization.id === ws.clerkOrgId)?.organization ??
          memberships.find((m) => ws?.name && m.organization.name === ws.name)?.organization;
        const organization = org?.id ?? ws?.clerkOrgId;
        if (!organization || !activate) throw new Error("Couldn't switch workspace");
        rememberSwitchedFor(documentId);
        await activate({ organization });
        // Saved to the session before the reload below, or the page would open in the old one.
        await touchSession(current);
      },
    };
  }, [documentId]);

  const thing = useMemo(() => fileThing({ id: documentId, name }), [documentId, name]);
  const search = useAutoWorkspaceSwitch(switcher, thing, {
    enabled: isLoaded && !afterSwitch,
    onSwitched: (workspace) => {
      // Everything the editor holds is the old workspace's: it starts again in the new one, on
      // this file, and says so once it has (ArtworkShareHost).
      rememberSwitchedWorkspace(workspace);
      window.location.reload();
    },
  });

  const looking = !afterSwitch && (!isLoaded || search.status === 'idle' || search.status === 'checking');
  const waiting = !looking && search.status !== 'switched';

  // A request just sent: ask again quickly, the answer is likely on its way.
  const watch = useRef<AccessWatch | null>(null);
  const adapter = useMemo<ShareAdapter>(
    () => ({
      ...artworkShareAdapter,
      requestAccess: async (thing, input) => {
        const sent = await artworkShareAdapter.requestAccess!(thing, input);
        watch.current?.hurry();
        return sent;
      },
    }),
    [],
  );
  const granted = useRef(onGranted);
  granted.current = onGranted;
  useEffect(() => {
    if (!waiting) return undefined;
    const w = watchForAccess(
      () => artworkCmd('artwork.doc.get', { documentId }).then(() => true),
      () => {
        if (mayOpenByItself(documentId)) granted.current();
      },
    );
    watch.current = w;
    return () => {
      w.stop();
      if (watch.current === w) watch.current = null;
    };
  }, [waiting, documentId]);

  if (looking || search.status === 'switched') {
    return (
      <p role="status" className="text-[13px] text-label-2">
        {search.status === 'switched' ? `Switching to ${search.workspace?.name || 'your other workspace'}…` : 'Looking for this file…'}
      </p>
    );
  }
  return (
    <>
      <AccessScreen state="needs-access" thing={thing} adapter={adapter} roles={['view', 'edit']} defaultRole="view" theme="artwork" mode={mode} />
      <Button variant="plain" icon="files" onClick={onHome}>
        All Files
      </Button>
    </>
  );
}
